/**
 * Blocks File Storage / DMS client.
 *
 * Uploads a file to Azure Blob Storage via a short-lived presigned URL, registers
 * it in the DMS, and lists files in a folder. Used to back photo upload for org
 * nodes (see `dataClient.integrations.Core.UploadFile` in `src/services/data.js`).
 *
 * Flow: GetPreSignedUrlForUpload → PUT bytes to Azure → UploadFile (register) →
 * GetDmsFileAndFolder (list). All calls except the blob PUT need the Blocks IAM
 * bearer token, taken from the existing `getAuthHeaders()` helper.
 */
import { getAuthHeaders, ensureFreshToken, refreshAccessToken, canRefresh } from '@/services/auth';

const BLOCKS_API_URL = (import.meta.env.VITE_BLOCKS_API_URL || 'https://blocksapi.slsblx.com').replace(/\/$/, '');
const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';

// DMS folder to upload into / list from. Empty string = tenant root (verified to
// work). Override with VITE_DMS_FOLDER_ID to target a specific folder GUID.
export const DEFAULT_FOLDER_ID = import.meta.env.VITE_DMS_FOLDER_ID || '';

function filesUrl(action) {
  return `${BLOCKS_API_URL}/data/v4/Files/${action}`;
}

async function postJson(url, body) {
  const doFetch = () =>
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-blocks-key': projectKey,
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    });

  await ensureFreshToken();
  let response = await doFetch();

  // On 401, refresh the token/session once and retry (same pattern as the gateway).
  if (response.status === 401 && canRefresh()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await doFetch();
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.isSuccess === false) {
    throw new Error(data.message || data.errors || 'File storage request failed');
  }
  return data;
}

/** Step 1: ask for a presigned Azure Blob upload URL. */
export async function getPreSignedUploadUrl(file, parentDirectoryId) {
  return postJson(filesUrl('GetPreSignedUrlForUpload'), {
    name: file.name,
    projectKey,
    configurationName: 'Default',
    accessModifier: 'Public',
    metaData: '',
    parentDirectoryId,
    tags: '',
    moduleName: 3,
  });
}

/** Step 2: PUT the raw bytes straight to Azure Blob Storage (SAS URL is the auth). */
export async function uploadBlob(uploadUrl, file) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-ms-blob-type': 'BlockBlob',
    },
    body: file,
  });
  if (!response.ok) throw new Error(`Blob upload failed: ${response.status}`);
}

/** Step 3: register the uploaded blob in the DMS. */
export async function registerUploadedFile(file, fileStorageId, parentId) {
  return postJson(filesUrl('UploadFile'), {
    upload: [
      {
        artifactName: file.name,
        description: `Uploaded file: ${file.name}`,
        parentId,
        tags: [],
        metaData: {},
        organizationId: '',
        fileStorageId,
        configurationName: 'Azure',
      },
    ],
    projectKey,
  });
}

/** Step 4: list files/folders under a given parent. */
export async function getDmsFileAndFolder(parentId = DEFAULT_FOLDER_ID, { skip = 0, take = 20 } = {}) {
  return postJson(filesUrl('GetDmsFileAndFolder'), {
    configurationName: 'Azure',
    projectKey,
    skip,
    take,
    parentId,
  });
}

/**
 * Run the full presign → upload → register flow for a single File.
 *
 * Because the file is uploaded with `accessModifier: 'Public'`, the blob is
 * anonymously readable at the SAS URL with its query string stripped — that base
 * URL is a stable, displayable download URL (verified against the live API).
 *
 * @returns {Promise<{ fileId: string, url: string }>}
 */
export async function uploadFile(file, parentDirectoryId = DEFAULT_FOLDER_ID) {
  if (!file) throw new Error('No file selected');
  const { uploadUrl, fileId } = await getPreSignedUploadUrl(file, parentDirectoryId);
  await uploadBlob(uploadUrl, file);
  await registerUploadedFile(file, fileId, parentDirectoryId);
  return { fileId, url: uploadUrl.split('?')[0] };
}

/**
 * Extract the DMS fileStorageId from a Blocks blob URL. Uploaded blobs live at
 * `…/<container>/Public/<fileStorageId>/<blobGuid>/<name>`, so the id is the GUID
 * right after `/Public/`. Returns '' for non-Blocks URLs (e.g. legacy base44).
 */
export function fileIdFromUrl(url) {
  const match = typeof url === 'string' && url.match(/\/Public\/([0-9a-fA-F-]{36})\//);
  return match ? match[1] : '';
}

/**
 * Delete a file from Blocks storage by its fileStorageId.
 * NOTE: DeleteFile uses configurationName 'Default' (like presign) — not 'Azure'.
 */
export async function deleteFile(fileId) {
  if (!fileId) return null;
  return postJson(filesUrl('DeleteFile'), {
    FileId: fileId,
    configurationName: 'Default',
    projectKey,
  });
}

/**
 * Delete a stored file given its public URL. No-op for empty or non-Blocks URLs
 * (e.g. legacy base44 links, which aren't in Blocks storage).
 */
export async function deleteFileByUrl(url) {
  const fileId = fileIdFromUrl(url);
  if (fileId) return deleteFile(fileId);
  return null;
}
